import { Controller } from "@hotwired/stimulus"

// Structured time-of-day segment editor for A/B "By time of day" mode.
// Builds schedule array [{ startMin, endMin, experienceId }] and writes to hidden input
// so ab-config-form#sync picks it up. 0–1440 minutes; display as HH:MM.
export default class extends Controller {
  static targets = ["output", "segmentList", "rowTemplate"]
  static values = {
    experienceIds: { type: Array, default: [] },
    experienceNames: { type: Object, default: {} }
  }

  connect() {
    if (!this.hasOutputTarget) return
    this.bindRowEvents()
    const rowCount = this.segmentListTarget?.querySelectorAll("[data-segment-row]").length ?? 0
    if (rowCount === 0 && this.experienceIdsValue.length > 0) {
      const ids = (this.experienceIdsValue || []).filter(Boolean)
      const n = ids.length
      const base = Math.floor(1440 / n)
      const rem = 1440 - base * n
      let cursor = 0
      const schedule = ids.map((experienceId, i) => {
        const width = base + (i < rem ? 1 : 0)
        const endMin = i === n - 1 ? 1440 : Math.min(1440, cursor + width)
        const seg = { startMin: cursor, endMin, experienceId }
        cursor = endMin
        return seg
      })
      this.outputTarget.value = JSON.stringify(schedule)
      this.renderRows(schedule)
    } else {
      this.syncFromRows()
    }
  }

  bindRowEvents() {
    if (!this.hasSegmentListTarget) return
    this.segmentListTarget.querySelectorAll("select, input").forEach((el) => {
      el.addEventListener("change", () => this.syncFromRows())
      el.addEventListener("input", () => this.syncFromRows())
    })
  }

  minutesToHHMM(min) {
    const m = Math.max(0, Math.min(1440, Math.round(Number(min) || 0)))
    const h = Math.floor(m / 60)
    const mm = m % 60
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
  }

  hhmmToMinutes(str) {
    if (!str || typeof str !== "string") return 0
    const match = str.trim().match(/^(\d{1,2}):(\d{2})$/)
    if (!match) return 0
    const h = parseInt(match[1], 10)
    const mm = parseInt(match[2], 10)
    return Math.max(0, Math.min(1440, h * 60 + mm))
  }

  getScheduleFromRows() {
    const schedule = []
    const rows = this.segmentListTarget.querySelectorAll("[data-segment-row]")
    rows.forEach((row) => {
      const expSelect = row.querySelector("[data-segment-exp]")
      const startInput = row.querySelector("[data-segment-start]")
      const endInput = row.querySelector("[data-segment-end]")
      const experienceId = expSelect?.value?.trim() || null
      const startMin = this.hhmmToMinutes(startInput?.value)
      const endMin = this.hhmmToMinutes(endInput?.value)
      if (experienceId && endMin > startMin) {
        schedule.push({ startMin, endMin, experienceId })
      }
    })
    schedule.sort((a, b) => a.startMin - b.startMin)
    return schedule
  }

  syncFromRows() {
    if (!this.hasOutputTarget) return
    const schedule = this.getScheduleFromRows()
    this.outputTarget.value = JSON.stringify(schedule)
    this.outputTarget.dispatchEvent(new CustomEvent("ab-config-form:sync", { bubbles: true }))
  }

  renderRows(schedule) {
    if (!this.hasSegmentListTarget) return
    const ids = this.experienceIdsValue || []
    const names = this.experienceNamesValue || {}
    if (schedule.length === 0 && ids.length > 0) {
      schedule = [{ startMin: 0, endMin: 1440, experienceId: ids[0] }]
    }
    this.segmentListTarget.innerHTML = schedule.map((seg) => this.segmentRowHTML(seg, ids, names)).join("")
    this.bindRowEvents()
  }

  segmentRowHTML(seg, experienceIds, experienceNames) {
    const expId = seg.experienceId || (experienceIds[0] || "")
    const expName = experienceNames[expId] || expId
    const startHHMM = this.minutesToHHMM(seg.startMin ?? 0)
    const endHHMM = seg.endMin >= 1440 ? "24:00" : this.minutesToHHMM(seg.endMin ?? 1440)
    return `
      <div class="flex flex-col gap-2 min-w-[200px] flex-1 max-w-[280px]" data-segment-row>
        <div class="rounded-lg px-3 py-2 bg-[#4a4971] border border-[#5a5978]">
          <span class="text-white text-small font-medium" title="${this.escapeHtml(expName)}">${this.escapeHtml(expName)}</span>
          <input type="hidden" data-segment-exp value="${this.escapeHtml(expId)}">
        </div>
        <div class="flex flex-col gap-2 rounded-lg border border-input bg-[#39385E] p-2">
          <div class="flex items-center justify-between rounded-md border border-input bg-input/50 px-3 py-2">
            <span class="text-small text-gray-400">From</span>
            <input type="text" data-segment-start value="${this.escapeHtml(startHHMM)}" placeholder="HH:MM" maxlength="5" class="w-14 bg-transparent border-0 text-right text-white font-bold text-small focus:outline-none focus:ring-0 p-0" data-action="input->time-of-day-editor#syncFromRows">
          </div>
          <div class="flex items-center justify-between rounded-md border border-input bg-input/50 px-3 py-2">
            <span class="text-small text-gray-400">To</span>
            <input type="text" data-segment-end value="${this.escapeHtml(endHHMM)}" placeholder="HH:MM" maxlength="5" class="w-14 bg-transparent border-0 text-right text-white font-bold text-small focus:outline-none focus:ring-0 p-0" data-action="input->time-of-day-editor#syncFromRows">
          </div>
        </div>
      </div>
    `
  }

  escapeHtml(s) {
    if (s == null) return ""
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }

  addSegment() {
    if (!this.hasSegmentListTarget) return
    const schedule = this.getScheduleFromRows()
    const ids = this.experienceIdsValue || []
    const lastEnd = schedule.length > 0 ? schedule[schedule.length - 1].endMin : 0
    const nextStart = Math.min(lastEnd, 1439)
    const nextEnd = Math.min(1440, nextStart + 60)
    const newSeg = {
      startMin: nextStart,
      endMin: nextEnd,
      experienceId: ids[0] || ""
    }
    schedule.push(newSeg)
    this.renderRows(schedule)
    this.syncFromRows()
  }

}
